/**
 * `entities/taxonomy-list` query options factories.
 *
 * Direct hey-api client calls by URL (these endpoints aren't in the generated
 * assets-api SDK yet), same convention as `entities/taxonomy`. `assetsHttp`
 * auto-injects `x-selected-workspace-id`, so workspace context is implicit.
 */
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { assetsHttp } from '@/lib/api/http-client';
import type {
  TaxonomyListChangeLogEntry,
  TaxonomyListDetail,
  TaxonomyListImpact,
  TaxonomyListIndexEntry,
  TaxonomyStandardListEntry,
} from '../model/types';
import { TAXONOMY_LIST_QUERY_KEYS } from './query-keys';

const LISTS_URL = '/api/v1/taxonomy-lists';

export const taxonomyListsQueryOptions = () =>
  queryOptions({
    queryKey: TAXONOMY_LIST_QUERY_KEYS.list(),
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({ throwOnError: true, url: LISTS_URL, signal });
      return (response.data as TaxonomyListIndexEntry[] | undefined) ?? [];
    },
  });

export const taxonomyListDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: TAXONOMY_LIST_QUERY_KEYS.detail(id),
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({
        throwOnError: true,
        url: `${LISTS_URL}/{id}`,
        path: { id },
        signal,
      });
      if (!response.data) {
        throw new Error('Failed to load list');
      }
      return response.data as TaxonomyListDetail;
    },
  });

/** How many live schemas bind a field to this list — gates deletion in the UI. */
export const taxonomyListUsageQueryOptions = (id: string) =>
  queryOptions({
    queryKey: TAXONOMY_LIST_QUERY_KEYS.usage(id),
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({
        throwOnError: true,
        url: `${LISTS_URL}/{id}/usage`,
        path: { id },
        signal,
      });
      return (response.data as { count: number } | undefined)?.count ?? 0;
    },
  });

/** The list's append-only audit trail — who moved the vocabulary, when and why. */
export const taxonomyListChangeLogQueryOptions = (id: string) =>
  queryOptions({
    queryKey: TAXONOMY_LIST_QUERY_KEYS.changeLog(id),
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({
        throwOnError: true,
        url: `${LISTS_URL}/{id}/change-log`,
        path: { id },
        signal,
      });
      return (response.data as TaxonomyListChangeLogEntry[] | undefined) ?? [];
    },
  });

/**
 * Blast radius: which schemas bind to this list and which will actually change
 * when a new version is published (following) versus stay frozen (pinned).
 */
export const taxonomyListImpactQueryOptions = (id: string) =>
  queryOptions({
    queryKey: TAXONOMY_LIST_QUERY_KEYS.impact(id),
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({
        throwOnError: true,
        url: `${LISTS_URL}/{id}/impact`,
        path: { id },
        signal,
      });
      return (
        (response.data as TaxonomyListImpact | undefined) ?? {
          followingCount: 0,
          pinnedCount: 0,
          schemas: [],
        }
      );
    },
  });

/** The Standard Lists catalog, flagged with what this org already holds. */
export const taxonomyStandardListCatalogQueryOptions = () =>
  queryOptions({
    queryKey: TAXONOMY_LIST_QUERY_KEYS.standardCatalog(),
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({
        throwOnError: true,
        url: `${LISTS_URL}/standard-catalog`,
        signal,
      });
      return (response.data as TaxonomyStandardListEntry[] | undefined) ?? [];
    },
  });

const blockingSchemasResponseSchema = z.object({
  data: z.array(
    z.object({
      taxonomyId: z.string(),
      taxonomyName: z.string(),
      fieldNames: z.array(z.string()),
      columnIds: z.array(z.string()),
      follows: z.boolean(),
      pinnedVersionId: z.string().nullable(),
    }),
  ),
});

/**
 * The schemas a delete of this list is refused for. Unlike the impact preview,
 * this counts published snapshots as well as head versions, as the server's
 * delete guard does.
 */
export const taxonomyListBlockingSchemasQueryOptions = (id: string) =>
  queryOptions({
    queryKey: TAXONOMY_LIST_QUERY_KEYS.blockingSchemas(id),
    // Refetched on every open; the dialog waits for that answer, not a cached one.
    staleTime: 0,
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({
        throwOnError: true,
        url: `${LISTS_URL}/{id}/blocking-schemas`,
        path: { id },
        signal,
      });
      // Parsed so a response the dialog cannot read shows its error, never "unused".
      return blockingSchemasResponseSchema.parse(response.data).data;
    },
  });
